import type { PortfolioDividendDataRaw } from '@/features/portfolio/hooks/usePortfolioDividend';
import type { PortfolioDividendForecastData } from '@/features/portfolio/hooks/usePortfolioHoldingsDividendForecast';
import type {
  DividendHoldingItem,
  PortfolioDividendHolding,
  PeriodData,
  NormalizedDividendQuality,
  Period,
} from '../types/portfolioDividend';
import { DIVIDEND_QUALITY_PERIOD_KEYS } from '../constants/portfolioDividend';

const sumOfPaymentForPeriod = (
  holdings: PortfolioDividendHolding[],
  period: Period
) => {
  return holdings.reduce((acc, holding) => {
    return acc + (holding.dividendForecast?.[period].payment || 0);
  }, 0);
};

const getHoldingsForPeriod = (
  holdings: PortfolioDividendHolding[],
  period: Period
) => {
  return holdings.map((holding): DividendHoldingItem => {
    const {
      company: { name, tickerSymbol, canonicalURL, score },
      dividendForecast,
      isAnalysisRestricted,
    } = holding;
    // TODO
    const periodRawData = dividendForecast?.[period] ?? {};

    const {
      payment,
      dividendYield,
      yieldOnCapitalInvested,
      dividendPaymentsGrowthAnnual,
    } = periodRawData;

    const sumPayment = sumOfPaymentForPeriod(holdings, period);
    const contribution = payment / sumPayment;
    const dividendScore = score?.dividend ?? null;

    return {
      score,
      name,
      tickerSymbol,
      dividendYield,
      payment,
      canonicalURL,
      yieldOnCapitalInvested,
      dividendPaymentsGrowthAnnual,
      contribution,
      dividendScore,
      isAnalysisRestricted,
    };
  });
};

const getSummaryStatistics = (dividendHoldings: DividendHoldingItem[]) => {
  const totalPayment = dividendHoldings.reduce((acc, holding) => {
    const payment = holding.payment || 0;
    return acc + payment;
  }, 0);
  const totalContribution = dividendHoldings.reduce((acc, holding) => {
    const contribution = holding.contribution || 0;
    return acc + contribution;
  }, 0);

  return {
    totalPayment,
    totalContribution,
    totalHoldingCount: dividendHoldings.length,
  };
};

type GetSummaryStatisticsByScoreParams = {
  min?: number;
  max?: number;
};
const getSummaryStatisticsByScore =
  ({ min = -Infinity, max = Infinity }: GetSummaryStatisticsByScoreParams) =>
  (dividendHoldings: DividendHoldingItem[]) => {
    const filteredHoldings = dividendHoldings.filter((holding) => {
      return (
        holding.dividendScore !== null &&
        holding.dividendScore >= min &&
        holding.dividendScore <= max
      );
    });

    return getSummaryStatistics(filteredHoldings);
  };

const getLowScoreData = getSummaryStatisticsByScore({ max: 2 });

const getMediumScoreData = getSummaryStatisticsByScore({ min: 3, max: 4 });

const getHighScoreData = getSummaryStatisticsByScore({ min: 5 });

export const normalizeDividendQuality = (
  holdings: PortfolioDividendHolding[],
  period: Period
): PeriodData => {
  const validHoldings = holdings.filter(
    (holding) => holding.isAnalysisRestricted !== true
  );
  const excludedHoldings = holdings.filter(
    (holding) => holding.isAnalysisRestricted === true
  );

  const excludedDividendHoldings = getHoldingsForPeriod(
    excludedHoldings,
    period
  );
  const validDividendHoldings = getHoldingsForPeriod(validHoldings, period);

  return {
    totalPayment: sumOfPaymentForPeriod(validHoldings, period),
    lowScore: getLowScoreData(validDividendHoldings),
    mediumScore: getMediumScoreData(validDividendHoldings),
    highScore: getHighScoreData(validDividendHoldings),
    holdings: validDividendHoldings,
    excludedHoldings: excludedDividendHoldings,
  };
};

export const extractDividendQualityByPeriod = (
  rawResponse?: PortfolioDividendForecastData
): NormalizedDividendQuality => {
  const holdings = rawResponse?.portfolioHoldingsDividendForecast;
  if (!holdings) {
    return {};
  }

  return DIVIDEND_QUALITY_PERIOD_KEYS.reduce(
    (acc, period) => ({
      ...acc,
      [period]: normalizeDividendQuality(holdings, period),
    }),
    {}
  );
};

export const extractKeyMetrics = (rawResponse?: PortfolioDividendDataRaw) => {
  const data = rawResponse?.Portfolio;

  if (!data) {
    return null;
  }

  const {
    holdingsCount,
    analysis: {
      estimatedYearlyDividend,
      estimatedYearlyDividendPast1y,
      estimatedYearlyDividendYieldOnCapitalInvested,
      estimatedDividendYield,
    },
  } = data;

  const estGrowthRate = estimatedYearlyDividend
    ? (estimatedYearlyDividend - estimatedYearlyDividendPast1y) /
      estimatedYearlyDividendPast1y
    : null;

  return {
    holdingsCount,
    estGrowthRate,
    estIncome12m: estimatedYearlyDividend,
    estCurrentYield: estimatedDividendYield,
    estYieldOnCost: estimatedYearlyDividendYieldOnCapitalInvested,
  };
};
